import { createApi } from "@reduxjs/toolkit/query/react";
import fetchBaseQueryCSRF from "../helper";

export type ExperimentStatus = "running" | "completed";

export interface StrategyActivation {
    // null before the switch of a scheduled experiment (lot 3)
    scraping_id: number | null;
    mode: string | null;
    bidding_strategy_type: string | null;
    parameters: Record<string, unknown>;
}

export interface Experiment {
    id: number;
    workspace_id: number;
    region_name: string;
    platform: string;
    campaign_id: string;
    campaign_name: string;
    name: string;
    start_date: string;
    end_date: string | null;
    comparison_weeks: number;
    // "scheduled" (lot 3) is listed in the Ongoing experiments block
    status: string;
    origin: string;
    // null: not found in scraping_history
    strategy_before: StrategyActivation[] | null;
    strategy_after: StrategyActivation[] | null;
    created_at: string;
    // Lot 1b: failed strategy switch
    switch_error_step: string | null;
    switch_error_message: string | null;
    rollback_status: "success" | "failed" | null;
}

export interface ExperimentList {
    page: number;
    page_size: number;
    total: number;
    data: Experiment[];
}

export interface ExperimentDailyRow {
    date_day: string;
    period: "before" | "excluded" | "after";
    ad_cost: number;
    paid_clicks: number;
    organic_clicks: number;
    brand_traffic: number;
    brand_searches: number;
    blended_cpc: number | null;
    blended_ctr: number | null;
    // null on the rows computed before the conversions were added
    paid_conversions: number | null;
    paid_cvr: number | null;
}

export interface ExperimentDaily {
    experiment_id: number;
    snapshot_date: string | null;
    computed_at: string | null;
    rows: ExperimentDailyRow[];
}

// Lot 2: Causal Impact, precomputed by step 3 of the 13:00 job
// Modelled series of AI.CAUSAL_EFFECT; Blended CTR, Blended CPC and CVR Paid are derived from them
export type CausalModelledKey = "brand_traffic" | "ad_cost" | "paid_clicks" | "organic_clicks" | "paid_conversions";
export type CausalMetricKey = CausalModelledKey | "blended_ctr" | "blended_cpc" | "paid_cvr";

export interface CausalEffectRow {
    metric: CausalMetricKey;
    date_day: string;
    is_post_intervention: boolean;
    actual: number | null;
    // null before the start date
    predicted: number | null;
    lower_bound: number | null;
    upper_bound: number | null;
}

/** Incremental value of a metric over the after period */
export interface CausalIncrementalMetric {
    metric: CausalMetricKey;
    // Volumes: SUM(actual) - SUM(predicted); ratios: gap of the ratios on the sums
    value: number | null;
    pct: number | null;
    // p-value the effect is read with (the highest of its sources for a ratio), null if none
    p_value: number | null;
    // Error of the modelled metrics it depends on, null if OK
    status: string | null;
}

/** "Incremental" row, totals over the after period */
export interface CausalIncremental {
    post_days: number;
    metrics: CausalIncrementalMetric[];
}

export interface ExperimentCausalEffect {
    experiment_id: number;
    snapshot_date: string | null;
    rows: CausalEffectRow[];
    // null until the job has computed the experiment (14 days after its start)
    incremental: CausalIncremental | null;
    // Error message of a modelled metric, null if OK
    status: Record<CausalModelledKey, string | null>;
}

export interface Campaign {
    campaign_id: string;
    campaign_name: string;
    account_id: string | null;
    // Lot 1c: Max CPC is only offered for "Search"
    advertising_channel_type?: string | null;
}

// Lot 1c: Signal modes of a switch to Max CPC
export type MaxCpcMode = "Active Mode" | "KO Bidding Manual" | "KO Bidding Auto";
export type KoAutoKpi = "Smart Top" | "Smart First" | "Paid Top" | "Paid First";

/** Enabled keyword of the campaign, with the modes it can take. */
export interface CampaignKeyword {
    keyword_id: string;
    keyword: string;
    match_type: string;
    ad_group_id: string;
    ad_group_name: string;
    campaign_name: string;
    modes: MaxCpcMode[];
}

export interface MaxCpcKeywordInput {
    keyword_id: string;
    ad_group_id: string;
    initial_max_cpc: number | null;
    max_cpc: number | null;
    kpi: KoAutoKpi | null;
    threshold: number | null;
}

export type ExperimentStrategy =
    | "Target ROAS"
    | "Maximize Conversion Value"
    | "Target CPA"
    | "Maximize Conversions"
    | "Target Impression Share"
    | "Max CPC";

export interface ExperimentCreate {
    workspace_id: number;
    region_name: string;
    name: string;
    campaign_id: string;
    start_date: string;
    end_date: string | null;
    comparison_weeks: number;
    // Lot 1b: strategy switch (no end date or a future one)
    strategy?: ExperimentStrategy;
    target_roas?: number | null;
    target_cpa?: number | null;
    max_cpc?: number | null;
    tis?: number | null;
    position?: string | null;
    // Lot 1c: switch to Max CPC
    mode?: MaxCpcMode;
    keywords?: MaxCpcKeywordInput[];
}

const JSON_HEADERS = {
    "Content-Type": "application/json",
    Accept: "application/json",
};

const bidStratTesting = createApi({
    baseQuery: fetchBaseQueryCSRF({ baseUrl: process.env.CROSS_API_URL }),
    reducerPath: "api.bidStratTesting",
    tagTypes: ["experiments"],
    endpoints: (builder) => ({
        // Preprod calls the real Google Ads accounts: the API restricts it to one client
        getAvailability: builder.query<{ env: string; restricted_to_client: string | null }, void>({
            query: () => ({ url: "/bid-strat-testing/availability", method: "GET" }),
        }),
        getExperiments: builder.query<
            ExperimentList,
            { workspace_id: number; region_name: string; status: ExperimentStatus; page: number; page_size: number }
        >({
            query: (body) => ({
                url: "/bid-strat-testing/experiments/list",
                method: "POST",
                body: JSON.stringify(body),
                headers: JSON_HEADERS,
            }),
            providesTags: ["experiments"],
        }),

        getExperimentCampaigns: builder.query<{ data: Campaign[] }, { workspace_id: number; region_name: string }>({
            query: (body) => ({
                url: "/bid-strat-testing/campaigns",
                method: "POST",
                body: JSON.stringify(body),
                headers: JSON_HEADERS,
            }),
        }),

        createExperiment: builder.mutation<Experiment, ExperimentCreate>({
            query: (body) => ({
                url: "/bid-strat-testing/experiments",
                method: "POST",
                body: JSON.stringify(body),
                headers: JSON_HEADERS,
            }),
            invalidatesTags: ["experiments"],
        }),

        // G-READ: "Your current strategy" of the modal step 2
        getCampaignStrategy: builder.query<StrategyActivation, { workspace_id: number; region_name: string; campaign_id: string }>({
            query: ({ workspace_id, region_name, campaign_id }) => ({
                url: `/bid-strat-testing/campaigns/${encodeURIComponent(campaign_id)}/strategy`,
                method: "GET",
                params: { workspace_id, region_name },
                headers: { Accept: "application/json" },
            }),
        }),

        getCampaignKeywords: builder.query<{ data: CampaignKeyword[] }, { workspace_id: number; region_name: string; campaign_id: string }>({
            query: ({ workspace_id, region_name, campaign_id }) => ({
                url: `/bid-strat-testing/campaigns/${encodeURIComponent(campaign_id)}/keywords`,
                method: "GET",
                params: { workspace_id, region_name },
                headers: { Accept: "application/json" },
            }),
        }),

        // Lot 3: a scheduled experiment, before its switch
        cancelExperiment: builder.mutation<Experiment, number>({
            query: (experimentId) => ({
                url: `/bid-strat-testing/experiments/${experimentId}/cancel`,
                method: "POST",
                headers: JSON_HEADERS,
            }),
            invalidatesTags: ["experiments"],
        }),

        endExperiment: builder.mutation<Experiment, number>({
            query: (experimentId) => ({
                url: `/bid-strat-testing/experiments/${experimentId}/end`,
                method: "POST",
                headers: JSON_HEADERS,
            }),
            invalidatesTags: ["experiments"],
        }),

        // Precomputed by the 13:00 job: one read per experiment, the selectors
        // work on the cached rows
        getExperimentDaily: builder.query<ExperimentDaily, number>({
            query: (experimentId) => ({
                url: `/bid-strat-testing/experiments/${experimentId}/daily`,
                method: "GET",
                headers: { Accept: "application/json" },
            }),
        }),

        // Statistical graph (lot 2): read only when that view is shown
        getExperimentCausalEffect: builder.query<ExperimentCausalEffect, number>({
            query: (experimentId) => ({
                url: `/bid-strat-testing/experiments/${experimentId}/causal-effect`,
                method: "GET",
                headers: { Accept: "application/json" },
            }),
        }),
    }),
});

export const {
    useGetAvailabilityQuery,
    useGetExperimentsQuery,
    useGetExperimentCampaignsQuery,
    useCreateExperimentMutation,
    useGetExperimentDailyQuery,
    useGetExperimentCausalEffectQuery,
    useGetCampaignStrategyQuery,
    useGetCampaignKeywordsQuery,
    useEndExperimentMutation,
    useCancelExperimentMutation,
} = bidStratTesting;

export default bidStratTesting;
